import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { LoadingOverlay as LoadingOverlayImport } from '../../../../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { AppState } from '../../../../../../../stores/redux-store';
import { createCredentials as createCredentialsConnect } from '../../../../../../../connectivity/services/credentials';
import { ConsumedHandoffSession } from '../../../../../../../models/handoffSession';
import { buildHandoffCompletedUrl } from '../../../../../../../routes';
import { useProduct } from '../../../../../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { useVersion } from '../../../../../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { useCreateTransfer as useCreateTransferImport } from '../../../../../hooks/useCreateTransfer/useCreateTransfer';
import { useSelectedProvider } from '../../../../../../../components/App/ProviderContainer/useSelectedProvider';

type HandoffPayInitiatorProps = PropsFromRedux & {
  useCreateTransfer?: typeof useCreateTransferImport;
  LoadingOverlay?: React.ComponentType;
};

export const HandoffPayInitiatorPresenter: React.FC<HandoffPayInitiatorProps> = ({
  handoffSession,
  createCredentials,
  credentials,
  session,
  useCreateTransfer = useCreateTransferImport,
  LoadingOverlay = LoadingOverlayImport,
}: HandoffPayInitiatorProps) => {
  const { selectedProvider: provider } = useSelectedProvider();
  const { productType, productDimension, productNames } = useProduct();
  const { createTransfer } = useCreateTransfer();
  const credentialsId = credentials?.id;
  const {
    version: { version },
  } = useVersion();

  const sourceAccountNumber = session?.sourceAccountNumber;
  const appUri = useMemo(() => {
    const { userAccessToken, sessionId } = handoffSession;

    return buildHandoffCompletedUrl({
      version,
      productType,
      userAccessToken,
      trackingId: sessionId,
      productDimension,
    });
  }, [handoffSession, version, productDimension, productType]);

  useEffect(() => {
    createCredentials({
      credentialsRequest: {
        providerName: provider.name,
        fields: {},
        appUri,
        triggerRefresh: false,
        productNames,
      },
      refreshableItems: [],
      instantSign: false,
    });
  }, [createCredentials, appUri, provider, productNames]);

  useEffect(() => {
    if (credentialsId) {
      createTransfer({
        credentialsId,
        sourceUri: sourceAccountNumber,
        appUri,
      });
    }
  }, [createTransfer, credentialsId, appUri, sourceAccountNumber]);

  return <LoadingOverlay />;
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
    session: state.session.data,
    handoffSession: state.handoffSession.data as ConsumedHandoffSession,
  };
};

const mapDispatchToProps = {
  createCredentials: createCredentialsConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const HandoffPayInitiator = connector(HandoffPayInitiatorPresenter);
